/**
 * Maps animal slugs to generic track icon types.
 * 15 track types cover all 132 species by category.
 * Icons at: /images/tracks/{type}.png (128x128 sepia PNGs)
 */

const TRACK_MAP: Record<string, string> = {
  // Large paw (bears, wolves, hyenas, big dogs)
  'grizzly-bear': 'paw-large',
  'gray-wolf': 'paw-large',
  'polar-bear': 'paw-large',
  wolverine: 'paw-large',
  'african-wild-dog': 'paw-large',
  hyena: 'paw-large',
  'musk-ox': 'paw-large',
  wombat: 'paw-large',
  'tasmanian-devil': 'paw-large',
  'wolverine-frog': 'paw-large',

  // Small paw (foxes, small mammals, rodents)
  'red-fox': 'paw-small',
  'arctic-fox': 'paw-small',
  'fennec-fox': 'paw-small',
  coyote: 'paw-small',
  badger: 'paw-small',
  hedgehog: 'paw-small',
  porcupine: 'paw-small',
  numbat: 'paw-small',
  capybara: 'paw-small',
  'red-panda': 'paw-small',
  otter: 'paw-small',
  'dik-dik': 'paw-small',
  warthog: 'paw-small',
  'wild-boar': 'paw-small',
  'kiwi-bird': 'paw-small',

  // Cat paw (all felines)
  'snow-leopard': 'paw-cat',
  'mountain-lion': 'paw-cat',
  lion: 'paw-cat',
  tiger: 'paw-cat',
  jaguar: 'paw-cat',
  leopard: 'paw-cat',
  'black-panther': 'paw-cat',
  'siberian-tiger': 'paw-cat',
  'clouded-leopard': 'paw-cat',
  'amur-leopard': 'paw-cat',
  ocelot: 'paw-cat',
  caracal: 'paw-cat',
  'puma-black': 'paw-cat',
  lynx: 'paw-cat',
  cheetah: 'paw-cat',

  // Split hoof (deer, antelope, goats, cattle)
  moose: 'hoof-split',
  elk: 'hoof-split',
  'caribou-reindeer': 'hoof-split',
  'red-deer-stag': 'hoof-split',
  ibex: 'hoof-split',
  markhor: 'hoof-split',
  'mountain-goat': 'hoof-split',
  'ram-bighorn-sheep': 'hoof-split',
  bison: 'hoof-split',
  yak: 'hoof-split',
  giraffe: 'hoof-split',
  antelope: 'hoof-split',
  gazelle: 'hoof-split',
  impala: 'hoof-split',
  kudu: 'hoof-split',
  'sable-antelope': 'hoof-split',
  hartebeest: 'hoof-split',
  'arabian-oryx': 'hoof-split',
  hippopotamus: 'hoof-split',
  rhinoceros: 'hoof-split',
  'white-rhinoceros': 'hoof-split',
  camel: 'hoof-split',
  tapir: 'hoof-split',
  'tapir-malayan': 'hoof-split',
  okapi: 'hoof-split',

  // Horse hoof (equines)
  'white-horse': 'hoof-horse',
  'stallion-running': 'hoof-horse',
  'przewalskis-horse': 'hoof-horse',
  'arabian-stallion': 'hoof-horse',
  zebra: 'hoof-horse',

  // Bird foot (raptors, wading birds, land birds)
  'bald-eagle': 'bird-foot',
  'golden-eagle': 'bird-foot',
  'harpy-eagle': 'bird-foot',
  'harpy-eagle-in-flight': 'bird-foot',
  'sea-eagle': 'bird-foot',
  'peregrine-falcon': 'bird-foot',
  'falcon-in-flight': 'bird-foot',
  'barn-owl': 'bird-foot',
  'snowy-owl': 'bird-foot',
  condor: 'bird-foot',
  'andean-condor': 'bird-foot',
  'bearded-vulture': 'bird-foot',
  'secretary-bird': 'bird-foot',
  crane: 'bird-foot',
  cassowary: 'bird-foot',
  shoebill: 'bird-foot',
  'bald-ibis': 'bird-foot',
  kingfisher: 'bird-foot',
  toucan: 'bird-foot',
  quetzal: 'bird-foot',
  'resplendent-quetzal-in-flight': 'bird-foot',
  'frigate-bird': 'bird-foot',

  // Webbed bird foot (waterfowl, penguins)
  'emperor-penguin': 'bird-webbed',
  swan: 'bird-webbed',
  flamingo: 'bird-webbed',
  'snow-goose': 'bird-webbed',
  'mandarin-duck': 'bird-webbed',
  'snowy-egret': 'bird-webbed',
  albatross: 'bird-webbed',
  peacock: 'bird-webbed',
  'peacock-strutting': 'bird-webbed',
  'peafowl-indian': 'bird-webbed',

  // Fin/tail (whales, dolphins, sharks)
  'humpback-whale': 'fin-tail',
  'blue-whale': 'fin-tail',
  narwhal: 'fin-tail',
  'orca-killer-whale': 'fin-tail',
  dolphin: 'fin-tail',
  'great-white-shark': 'fin-tail',
  'hammerhead-shark': 'fin-tail',
  swordfish: 'fin-tail',
  marlin: 'fin-tail',

  // Flipper (seals, manatees, rays)
  walrus: 'fin-flipper',
  manatee: 'fin-flipper',
  stingray: 'fin-flipper',
  'sea-turtle': 'shell-turtle',

  // Reptile claw
  crocodile: 'claw-reptile',
  chameleon: 'claw-reptile',
  'thorny-devil': 'claw-reptile',

  // Dragon claw (large reptiles)
  'komodo-dragon': 'claw-dragon',
  'komodo-monitor': 'claw-dragon',

  // Primate hand
  orangutan: 'hand-primate',
  mandrill: 'hand-primate',
  lemur: 'hand-primate',

  // Snake trail
  'king-cobra': 'trail-snake',
  'timber-rattlesnake': 'trail-snake',

  // Unique
  axolotl: 'unique-axolotl',
  platypus: 'unique-axolotl',
  'mantis-shrimp': 'claw-reptile',
  pangolin: 'claw-reptile',
  sloth: 'hand-primate',
  koala: 'hand-primate',
  kangaroo: 'paw-large',
};

/** Default track for species not explicitly mapped */
const DEFAULT_TRACK = 'paw-small';

/** Get the track icon path for a species slug */
export function getTrackIcon(slug: string): string {
  const type = TRACK_MAP[slug] || DEFAULT_TRACK;
  return `/images/tracks/${type}.png`;
}
